import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { CATEGORIES, REGIONS, LISTING_TYPES } from "../lib/format";
import ListingCard from "../components/ListingCard";

export default function Browse() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favIds, setFavIds] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const category = params.get("category") || "all";
  const region = params.get("region") || "";
  const listing_type = params.get("listing_type") || "";
  const q = params.get("q") || "";
  const min_price = params.get("min_price") || "";
  const max_price = params.get("max_price") || "";
  const sort = params.get("sort") || "recent";

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };

  useEffect(() => {
    setLoading(true);
    const apiParams = {};
    if (category !== "all") apiParams.category = category;
    if (region) apiParams.region = region;
    if (listing_type) apiParams.listing_type = listing_type;
    if (q) apiParams.q = q;
    if (min_price) apiParams.min_price = min_price;
    if (max_price) apiParams.max_price = max_price;
    apiParams.sort = sort;
    api.get("/listings", { params: apiParams })
      .then((r) => setListings(r.data))
      .finally(() => setLoading(false));
  }, [category, region, listing_type, q, min_price, max_price, sort]);

  useEffect(() => {
    if (user) api.get("/favorites/ids").then((r) => setFavIds(r.data)).catch(() => {});
  }, [user]);

  const toggleFav = useCallback(async (id) => {
    if (!user) { navigate("/login"); return; }
    const res = await api.post(`/favorites/${id}`);
    setFavIds((prev) => (res.data.favorited ? [...prev, id] : prev.filter((x) => x !== id)));
  }, [user, navigate]);

  const resetFilters = () => setParams(new URLSearchParams());

  const Filters = (
    <div className="space-y-8" data-testid="filters-panel">
      <FilterGroup label={t("category")}>
        <FilterOption active={category === "all"} onClick={() => setParam("category", "")} label={t("all")} testid="filter-cat-all" />
        {CATEGORIES.map((c) => (
          <FilterOption key={c.key} active={category === c.key} onClick={() => setParam("category", c.key)} label={c[lang]} testid={`filter-cat-${c.key}`} />
        ))}
      </FilterGroup>

      <FilterGroup label={t("type")}>
        <FilterOption active={!listing_type} onClick={() => setParam("listing_type", "")} label={t("all")} testid="filter-type-all" />
        {LISTING_TYPES.map((ty) => (
          <FilterOption key={ty.key} active={listing_type === ty.key} onClick={() => setParam("listing_type", ty.key)} label={ty[lang]} testid={`filter-type-${ty.key}`} />
        ))}
      </FilterGroup>

      <FilterGroup label={t("region")}>
        <select
          data-testid="filter-region"
          value={region}
          onChange={(e) => setParam("region", e.target.value)}
          className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-gold"
        >
          <option value="">{t("all")}</option>
          {REGIONS.map((r) => <option key={r.value} value={r.value}>{r[lang]}</option>)}
        </select>
      </FilterGroup>

      <FilterGroup label={t("price_range")}>
        <div className="flex gap-2">
          <input data-testid="filter-min-price" type="number" placeholder={t("min")} value={min_price} onChange={(e) => setParam("min_price", e.target.value)} className="w-1/2 bg-black/40 border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-gold" />
          <input data-testid="filter-max-price" type="number" placeholder={t("max")} value={max_price} onChange={(e) => setParam("max_price", e.target.value)} className="w-1/2 bg-black/40 border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-gold" />
        </div>
      </FilterGroup>

      <button onClick={resetFilters} data-testid="reset-filters-btn" className="text-white/50 text-sm hover:text-gold transition-colors">{t("reset")}</button>
    </div>
  );

  return (
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-24 pb-16">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <h1 className="font-display text-3xl md:text-4xl text-white">
          {category === "all" ? (lang === "en" ? "All Listings" : "Toutes les annonces") : CATEGORIES.find((c) => c.key === category)?.[lang]}
        </h1>
        <div className="flex items-center gap-3">
          <select
            data-testid="sort-select"
            value={sort}
            onChange={(e) => setParam("sort", e.target.value)}
            className="bg-black/40 border border-white/15 px-3 py-2 text-sm text-white outline-none focus:border-gold"
          >
            <option value="recent">{t("sort_recent")}</option>
            <option value="price_low">{t("sort_low")}</option>
            <option value="price_high">{t("sort_high")}</option>
          </select>
          <button onClick={() => setShowFilters(true)} className="lg:hidden flex items-center gap-2 border border-white/15 px-4 py-2 text-sm text-white" data-testid="mobile-filters-btn">
            <SlidersHorizontal size={16} /> {t("filters")}
          </button>
        </div>
      </div>

      <div className="flex gap-10">
        <aside className="hidden lg:block w-64 shrink-0">{Filters}</aside>

        <div className="flex-1 min-w-0">
          <p className="text-white/40 text-sm mb-5" data-testid="results-count">{listings.length} {t("results")}</p>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => <div key={i} className="aspect-[4/3] shimmer" />)}
            </div>
          ) : listings.length === 0 ? (
            <div className="text-center py-24 border border-white/10 bg-[#121212]">
              <p className="text-white/50">{t("no_results")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {listings.map((l, i) => (
                <ListingCard key={l.id} listing={l} index={i} favorited={favIds.includes(l.id)} onToggleFav={toggleFav} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Mobile filter drawer */}
      {showFilters && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowFilters(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-80 max-w-[85vw] bg-[#0A0A0A] border-l border-white/10 p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white font-display text-xl">{t("filters")}</h3>
              <button onClick={() => setShowFilters(false)}><X className="text-white" /></button>
            </div>
            {Filters}
          </div>
        </div>
      )}
    </div>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div>
      <h4 className="text-white/80 text-xs uppercase tracking-wider mb-3">{label}</h4>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function FilterOption({ active, onClick, label, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`block w-full text-left text-sm py-1.5 px-2 transition-colors ${active ? "text-gold" : "text-white/55 hover:text-white"}`}>
      {label}
    </button>
  );
}
